import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  useRoutes,
} from 'react-router-dom';
import './app/styles/globalStyles.css';

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-api-key'];
const SENSITIVE_BODY_KEYS = [
  'password',
  'taxpayer_id',
  'taxpayerId',
  'phone',
  'email',
  'token',
];

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) =>
      SENSITIVE_BODY_KEYS.includes(key.toLowerCase())
        ? [key, '[Filtered]']
        : [key, value],
    ),
  );
}

function initSentry() {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    enabled: process.env.NODE_ENV !== 'development',
    sendDefaultPii: true,
    beforeSend(event) {
      if (event.request?.headers) {
        SENSITIVE_HEADERS.forEach((header) => {
          if (event.request!.headers![header]) {
            event.request!.headers![header] = '[Filtered]';
          }
        });
      }

      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrubObject(
          event.request.data as Record<string, unknown>,
        );
      }

      return event;
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.extraErrorDataIntegration({
        depth: 3,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.6,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(() => initSentry(), { timeout: 2000 });
} else {
  setTimeout(initSentry, 0);
}

export const useSentryRoutes = Sentry.wrapUseRoutesV6(useRoutes);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
